import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Alert } from "reactstrap";
import {
  Select,
  Input,
  Drawer,
  message as antdMessage,
  Button as AntButton,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { tasksApis } from "../../../api/NewTasks";
import { workApprovalApis } from "../../../api/WorkApproval";
import Swal from "sweetalert2";
import { EditAccess } from "../../../components/accessControl/AccessControl";

interface ChecklistViewAndUpdateModalProps {
  open: boolean;
  checklistId: number | null;
  checklistLinkId?: number | null;
  onClose: () => void;
  onSave?: (success: boolean) => void;
  isFromWorkUI?: boolean;
  checklistRecord?: any;
  onApprovalComplete?: () => void;
  initialEditMode?: boolean;
}

const TaskCheckListViewAndUpdateModal: React.FC<
  ChecklistViewAndUpdateModalProps
> = ({
  open,
  checklistId,
  checklistLinkId,
  onClose,
  onSave,
  isFromWorkUI,
  checklistRecord,
  onApprovalComplete,
  initialEditMode = false,
}) => {
  const { data: currentUser } = useSelector(
    (state: any) => state.currentUser
  );
  const isAdmin =
    (currentUser?.genericRole || "").toLowerCase() === "admin";

  const stateApprovals: any[] = checklistRecord?.stateApprovals || [];
  const isApprover =
    stateApprovals.length > 0 &&
    (stateApprovals.some(
      (a: any) =>
        a.userId === currentUser?.id &&
        (a.status === "Pending" || a.approvalStatus === "Pending")
    ) ||
      (isAdmin &&
        stateApprovals.some(
          (a: any) =>
            a.status === "Pending" || a.approvalStatus === "Pending"
        )));
  const hasPendingApproval = stateApprovals.some(
    (a: any) => a.status === "Pending" || a.approvalStatus === "Pending"
  );
  const isChecklistApproved =
    (checklistRecord?.approvalStatus || "").toLowerCase() === "approved" ||
    (stateApprovals.length > 0 &&
      stateApprovals.every(
        (a: any) =>
          (a.approvalStatus || a.status) === "Approved" ||
          (a.approvalStatus || a.status) === "Skipped"
      ));
  const [isApprovalProcessing, setIsApprovalProcessing] = useState(false);

  const handleApprove = async () => {
    const stateApproval = stateApprovals[0];
    if (!stateApproval) {
      antdMessage.error("Approval data not found");
      return;
    }
    const allVerificationsCompleted = stateApprovals.every(
      (a: any) =>
        a.approvalStatus === "Approved" ||
        a.stateApprovalId === stateApproval.stateApprovalId
    );
    if (!allVerificationsCompleted) {
      await Swal.fire({
        icon: "error",
        title: "Cannot Approve",
        text: "Action cannot be performed until all Checker/Verifier approvals are completed.",
        confirmButtonText: "OK",
      });
      return;
    }
    setIsApprovalProcessing(true);
    try {
      const response: any = await workApprovalApis.approveState({
        stateApprovalId: stateApproval.stateApprovalId,
      });
      const { status, message: msg } = response || {};
      if (status) {
        await Swal.fire({
          icon: "success",
          title: "Approved",
          text: "Checklist approved successfully",
          confirmButtonText: "OK",
        });
        onApprovalComplete?.();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: msg || "Failed to approve checklist",
          confirmButtonText: "OK",
        });
      }
    } catch (err: any) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message || "Failed to approve checklist",
        confirmButtonText: "OK",
      });
    } finally {
      setIsApprovalProcessing(false);
    }
  };

  const handleReject = async () => {
    const stateApproval = stateApprovals[0];
    if (!stateApproval) {
      antdMessage.error("Approval data not found");
      return;
    }
    const allVerificationsCompleted = stateApprovals.every(
      (a: any) =>
        a.approvalStatus === "Approved" ||
        a.stateApprovalId === stateApproval.stateApprovalId
    );
    if (!allVerificationsCompleted) {
      await Swal.fire({
        icon: "error",
        title: "Cannot Reject",
        text: "Action cannot be performed until all Checker/Verifier approvals are completed.",
        confirmButtonText: "OK",
      });
      return;
    }
    const { value: reason } = await Swal.fire({
      title: "Reject Checklist",
      input: "textarea",
      inputLabel: "Reason for rejection",
      inputPlaceholder: "Type your reason here...",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) return "Please input your reason!";
        return null;
      },
    });
    if (!reason) return;
    setIsApprovalProcessing(true);
    try {
      const response: any = await workApprovalApis.rejectState({
        stateApprovalId: stateApproval.stateApprovalId,
        reason,
      });
      const { status, message: msg } = response || {};
      if (status) {
        await Swal.fire({
          icon: "success",
          title: "Rejected",
          text: "Checklist rejected successfully",
          confirmButtonText: "OK",
        });
        onApprovalComplete?.();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: msg || "Failed to reject checklist",
          confirmButtonText: "OK",
        });
      }
    } catch (err: any) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message || "Failed to reject checklist",
        confirmButtonText: "OK",
      });
    } finally {
      setIsApprovalProcessing(false);
    }
  };

  const methods = useForm();
  const [answers, setAnswers] = useState<any>({});
  const [originalAnswers, setOriginalAnswers] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [checklist, setChecklist] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [answerIds, setAnswerIds] = useState<any>({});
  const [missingRequired, setMissingRequired] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Fetch checklist questions and answers
  useEffect(() => {
    if (open && checklistId && checklistLinkId) {
      setLoading(true);
      Promise.all([
        tasksApis.fetchCheckListById(checklistId),
        tasksApis.fetchChecklistAnswersByLinkId(checklistLinkId), // You will need to implement this API in NewTasks.tsx
      ])
        .then(([checklistRes, answersRes]) => {
          const baseChecklist = checklistRes?.status
            ? checklistRes.data
            : null;
          const answerMeta = answersRes?.status ? answersRes.data : null;

          if (baseChecklist) {
            setChecklist({
              ...baseChecklist,
              answeredByUserName: answerMeta?.answeredByUserName,
              answeredAt: answerMeta?.answeredAt || answerMeta?.updatedAt,
            });
          } else {
            setChecklist(null);
          }
          if (answersRes?.status) {
            // Map answers by questionId for easy access. Use String keys so
            // lookups work whether questionId is a number or a string.
            const answerMap: any = {};
            const answerIdMap: any = {};
            (answersRes.data.checklistAnswers || []).forEach((ans: any) => {
              const key = String(ans.questionId);
              answerMap[key] = ans.answer;
              answerIdMap[key] = ans.answerId;
            });
            setAnswers({ ...answerMap });
            setOriginalAnswers({ ...answerMap });
            setAnswerIds(answerIdMap);
            methods.reset({ answers: answerMap });
          } else {
            setAnswers({});
            setOriginalAnswers({});
            setAnswerIds({});
            methods.reset({ answers: {} });
          }
        })
        .finally(() => setLoading(false));
    } else {
      setChecklist(null);
      setAnswers({});
      setOriginalAnswers({});
      setAnswerIds({});
      methods.reset({ answers: {} });
    }
    setIsEditMode(initialEditMode);
    setMissingRequired(false);
    setApiError(null);
    // eslint-disable-next-line
  }, [checklistId, checklistLinkId, open, initialEditMode]);

  const handleAnswerChange = (questionId: number, value: any) => {
    const key = String(questionId);
    setAnswers((prev: any) => ({ ...prev, [key]: value }));
    methods.setValue(`answers.${key}`, value);
  };

  const handleUpdate = async () => {
    // Required field validation
    if (checklist && checklist.checklistQuestions) {
      const missing = checklist.checklistQuestions.some((q: any) => {
        const v = answers[String(q.questionId)];
        return (
          q.isMandatory &&
          (v === undefined ||
            v === null ||
            v === "" ||
            (Array.isArray(v) && v.length === 0))
        );
      });
      if (missing) {
        setMissingRequired(true);
        return;
      }
    }
    setMissingRequired(false);
    setApiError(null);
    try {
      // First-time fill: no existing answers — use submitChecklistAnswers
      const isInitialFill =
        Object.keys(originalAnswers || {}).length === 0 &&
        Object.keys(answerIds || {}).length === 0;
      if (isInitialFill) {
        const formattedAnswers =
          checklist?.checklistQuestions?.map((q: any) => {
            let v = answers[String(q.questionId)];
            if (q.questionType === "multiple_choice" && Array.isArray(v)) {
              v = v.join(",");
            }
            return {
              questionId: q.questionId,
              answer: v ?? "",
              remark: "",
            };
          }) || [];
        const submitPayload = {
          checklistLinkId,
          checklistId,
          answers: formattedAnswers,
        };
        const submitResp: any = await tasksApis.submitChecklistAnswers(
          submitPayload
        );
        const submitResult = submitResp?.response?.data ?? submitResp;
        if (submitResult?.status === true) {
          Swal.fire({
            icon: "success",
            title: "Checklist Submitted",
            text: "Checklist has been filled successfully!",
            timer: 2000,
            showConfirmButton: false,
          });
          if (onSave) onSave(true);
        } else {
          const errMsg =
            submitResult?.message ||
            submitResult?.error ||
            "Failed to submit checklist answers.";
          setApiError(errMsg);
          antdMessage.error(errMsg);
          if (onSave) onSave(false);
        }
        return;
      }
      // Normalize values for diffing — handle arrays (multi_choice) vs strings vs numbers
      const normalize = (v: any) => {
        if (v == null) return "";
        if (Array.isArray(v)) {
          return v
            .map((x) => String(x).trim())
            .filter(Boolean)
            .sort()
            .join(",");
        }
        if (typeof v === "string") {
          // Multi-choice answers come back as comma-separated strings; normalize order
          if (v.includes(",")) {
            return v
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean)
              .sort()
              .join(",");
          }
          return v.trim();
        }
        return String(v);
      };

      // Build the list of changed answers. Include changes for questions that
      // already have an answerId (existing answer being updated) AND for new
      // answers (no prior answerId — answerId omitted/null so backend can create).
      const updatedLines: any[] = [];
      Object.keys(answers).forEach((qid) => {
        if (normalize(answers[qid]) === normalize(originalAnswers[qid])) {
          return;
        }
        const question = checklist.checklistQuestions.find(
          (q: any) => String(q.questionId) === qid
        );
        let formattedAnswer = answers[qid];
        if (question?.questionType === "multiple_choice") {
          formattedAnswer = Array.isArray(answers[qid])
            ? answers[qid].join(",")
            : answers[qid];
        }
        const aid = answerIds[qid];
        const line: any = {
          answer: formattedAnswer,
          remark: "",
          questionId: Number(qid),
        };
        if (aid) line.answerId = aid;
        updatedLines.push(line);
      });
      if (updatedLines.length === 0) {
        setApiError("No changes detected. Please modify at least one answer before saving.");
        antdMessage.info("No changes detected.");
        if (onSave) onSave(false);
        return;
      }
      const payload = {
        checklistLinkId,
        checklistId,
        answers: { updatedLines },
      };
      const response = await tasksApis.updateChecklistAnswers(payload);
      // makeApiCall returns the API body on success but the raw AxiosError on
      // HTTP failure (4xx/5xx). The AxiosError carries `.status` (HTTP code)
      // which is truthy and would falsely match `if (response?.status)`.
      // Normalize by reading the actual API body from `response.response.data`
      // when present, then check the API's boolean `status` field explicitly.
      const apiResult = response?.response?.data ?? response;
      if (apiResult?.status === true) {
        setOriginalAnswers({ ...answers });
        setIsEditMode(false);
        setMissingRequired(false);
        Swal.fire({
          icon: "success",
          title: "Checklist Updated",
          text: "Checklist answers updated successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
        if (onSave) onSave(true);
      } else {
        const msg =
          apiResult?.message ||
          response?.message ||
          "Failed to update checklist answers.";
        setApiError(msg);
        antdMessage.error(msg);
        if (onSave) onSave(false);
      }
    } catch (error) {
      const msg = "An error occurred while updating checklist answers.";
      setApiError(msg);
      antdMessage.error(msg);
      if (onSave) onSave(false);
    }
  };

  const renderPreviewQuestion = (question: any) => {
    const answer = answers[String(question.questionId)];

    if (question.questionType === "text") {
      if (!isEditMode) {
        return <span>{answer || "-"}</span>;
      }
      return (
        <Input
          disabled={!isEditMode}
          placeholder={question.description || "Enter answer"}
          style={{ width: "100%" }}
          value={answer || ""}
          onChange={(e) =>
            handleAnswerChange(question.questionId, e.target.value)
          }
        />
      );
    }

    if (question.questionType === "single_choice") {
      if (!isEditMode) {
        // Show label for the answer
        return (
          <span>
            {question.optionsType?.[answer]
              ? question.optionsType[answer].replace(/"/g, "")
              : answer || "-"}
          </span>
        );
      }
      return (
        <Select
          disabled={!isEditMode}
          style={{ width: "100%" }}
          placeholder={question.description || "Select option"}
          options={
            question.optionsType
              ? Object.entries(question.optionsType).map(([key, val]) => ({
                  label: typeof val === "string" ? val.replace(/"/g, "") : val,
                  value: key,
                }))
              : []
          }
          value={answer || undefined}
          onChange={(value) => handleAnswerChange(question.questionId, value)}
        />
      );
    }

    if (question.questionType === "multiple_choice") {
      if (!isEditMode) {
        let selectedValues: string[] = [];
        if (typeof answer === "string") {
          selectedValues = answer.split(",").filter(Boolean);
        } else if (Array.isArray(answer)) {
          selectedValues = answer;
        }
        const labels = selectedValues
          .map((val: string) =>
            question.optionsType?.[val]
              ? question.optionsType[val].replace(/"/g, "")
              : val
          )
          .join(", ");
        return <span>{labels || "-"}</span>;
      }
      return (
        <Select
          disabled={!isEditMode}
          mode="multiple"
          style={{ width: "100%" }}
          placeholder={question.description || "Select options"}
          options={
            question.optionsType
              ? Object.entries(question.optionsType).map(([key, val]) => ({
                  label: typeof val === "string" ? val.replace(/"/g, "") : val,
                  value: key,
                }))
              : []
          }
          value={
            typeof answer === "string"
              ? answer.split(",")
              : Array.isArray(answer)
              ? answer
              : []
          }
          onChange={(value) => handleAnswerChange(question.questionId, value)}
        />
      );
    }
    return null;
  };

  return (
    <Drawer
      title={isEditMode ? "Edit Checklist Response" : "Checklist Response"}
      open={open}
      onClose={onClose}
      placement="right"
      width={760}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      extra={
        isEditMode ? (
          <div style={{ display: "flex", gap: 8 }}>
            <AntButton type="primary" size="small" onClick={handleUpdate}>
              Update
            </AntButton>
            <AntButton
              size="small"
              onClick={() => {
                setIsEditMode(false);
                setApiError(null);
                setMissingRequired(false);
              }}
            >
              Cancel
            </AntButton>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            {isApprover && (
              <>
                <AntButton
                  size="small"
                  style={{
                    backgroundColor: "#d1f5ec",
                    color: "#107154",
                    borderColor: "#d1f5ec",
                    fontWeight: 500,
                  }}
                  loading={isApprovalProcessing}
                  disabled={!hasPendingApproval}
                  onClick={handleApprove}
                >
                  Approve
                </AntButton>
                <AntButton
                  size="small"
                  style={{
                    backgroundColor: "#fbe3e4",
                    color: "#a94442",
                    borderColor: "#fbe3e4",
                    fontWeight: 500,
                  }}
                  loading={isApprovalProcessing}
                  disabled={!hasPendingApproval}
                  onClick={handleReject}
                >
                  Reject
                </AntButton>
              </>
            )}
            {!isChecklistApproved && (
              <EditAccess moduleName="Tasks">
                <AntButton
                  type="primary"
                  ghost
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setIsEditMode(true)}
                  disabled={isFromWorkUI}
                >
                  Edit
                </AntButton>
              </EditAccess>
            )}
          </div>
        )
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <span>Loading...</span>
        </div>
      ) : checklist ? (
        <FormProvider {...methods}>
          <form>
            <div className="mt-0 p-4">
              <div
                style={{
                  background: "#fafbfc",
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: "14px 16px",
                  marginBottom: 16,
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    fontSize: 18,
                    fontWeight: 600,
                    color: "#1f1f1f",
                  }}
                >
                  {checklist.checklistName}
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 16,
                    marginTop: 8,
                    fontSize: 13,
                    color: "#595959",
                  }}
                >
                  <span>
                    <strong>Check Points:</strong>{" "}
                    {checklist.checklistQuestions?.length || 0}
                  </span>
                  <span>
                    <strong>Responded by:</strong>{" "}
                    {checklist.answeredByUserName || "N/A"}
                  </span>
                  {checklist.answeredAt ? (
                    <span>
                      <strong>Responded on:</strong>{" "}
                      {new Date(checklist.answeredAt).toLocaleDateString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        }
                      )}
                    </span>
                  ) : null}
                </div>
              </div>
              {checklist.checklistQuestions?.length === 0 ? (
                <Alert color="info">No questions have been added yet.</Alert>
              ) : (
                <div className="preview-checklist">
                  {apiError && (
                    <Alert
                      color="danger"
                      style={{ marginBottom: 16 }}
                      toggle={() => setApiError(null)}
                    >
                      {apiError}
                    </Alert>
                  )}
                  {missingRequired && (
                    <Alert color="danger" style={{ marginBottom: 16 }}>
                      Please fill required fields.
                    </Alert>
                  )}
                  <table className="table table-bordered w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th
                          className="p-2 text-left font-semibold text-gray-900"
                          style={{ width: "30%", fontSize: "14px" }}
                        >
                          Question
                        </th>
                        <th
                          className="p-2 text-left font-semibold text-gray-900"
                          style={{ width: "30%", fontSize: "14px" }}
                        >
                          Responses
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {checklist.checklistQuestions?.map(
                        (question: any, index: number) => (
                          <tr
                            key={question.questionId || index}
                            className="border-t"
                          >
                            <td
                              className="p-2 text-gray-900 font-medium"
                              style={{ wordBreak: "break-word" }}
                            >
                              <div
                                className="text-black font-semibold d-flex align-items-center"
                                style={{ fontSize: "14px" }}
                              >
                                <span
                                  className="text-danger"
                                  style={{
                                    display: "inline-block",
                                    width: "12px",
                                    marginRight: "4px",
                                    textAlign: "center",
                                  }}
                                >
                                  {question.isMandatory ? "*" : ""}
                                </span>
                                {question.questionName || question.name}
                              </div>
                              {question.description && (
                                <div className="text-gray-800 mt-1">
                                  {question.description ||
                                    "No description provided"}
                                </div>
                              )}
                            </td>
                            <td className="p-2 text-gray-900 font-medium">
                              {renderPreviewQuestion(question)}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      ) : null}
    </Drawer>
  );
};

export default TaskCheckListViewAndUpdateModal;
